// Admin side of Get Paid: budget and stats, FeexPay deposit, payout settings
// and the three management tabs.
import { FormEvent, useEffect, useMemo, useState } from "react";
import { payoutsApi } from "@/api/payouts.api";
import { isAdminRole } from "@/lib/auth/roles";
import type { GetPaidManagementNavItem, GetPaidManagementTab } from "@/components/get-paid";
import type { BackendBudgetResponse, BackendPaymentSettings, BackendPaymentStats, PaymentNetwork } from "@/lib/types/payments";
import { formatCurrency } from "../helpers";
import { apiErrorMessage, splitPayerName } from "../logic/payout-logic";
import { isAdminOrSuper, type GetPaidBase, type MoneyFormat } from "./get-paid-context";

type DepositForm = { amount: string; phone: string; network: PaymentNetwork; description: string };

export const DEFAULT_PAYOUT_SETTINGS: BackendPaymentSettings = {
  contributorAmount: 5,
  contributorCap: 1000,
  contributorBudget: 0,
  moderatorAmount: 10,
  moderatorCount: 10,
  autoPayModerators: true,
  communityCompensationBasis: "contribution",
  communityAmountPerContribution: 5,
  communityAmountPerRecordedMinute: 5,
};

export function useAdminBudget({ t, user, toast, locale, currency, language }: GetPaidBase & MoneyFormat & { language: string }) {
  const emptyDeposit = (): DepositForm => ({ amount: "", phone: user?.phoneNumber ?? "", network: "FLOOZ", description: "" });
  const [loadingBudget, setLoadingBudget] = useState(false);
  const [budgetInfo, setBudgetInfo] = useState<BackendBudgetResponse | null>(null);
  const [paymentStats, setPaymentStats] = useState<BackendPaymentStats | null>(null);
  const [depositDialogOpen, setDepositDialogOpen] = useState(false);
  const [depositLoading, setDepositLoading] = useState(false);
  const [depositForm, setDepositForm] = useState<DepositForm>(emptyDeposit);
  const [payoutSettings, setPayoutSettings] = useState<BackendPaymentSettings>(DEFAULT_PAYOUT_SETTINGS);
  const [adminTab, setAdminTab] = useState<GetPaidManagementTab>("configuration");

  const adminNavigationItems = useMemo<GetPaidManagementNavItem[]>(() => [
    {
      value: "configuration",
      label: t.getPaid.configuration,
      description: language === "fr" ? "Budget, tarifs et seuils de paiement." : "Budget, rates, and payout thresholds.",
      icon: "settings",
    },
    {
      value: "moderators",
      label: t.getPaid.moderatorManagement,
      description: language === "fr" ? "Demandes et gains des modérateurs." : "Moderator requests and earnings.",
      icon: "shield",
    },
    {
      value: "contributors",
      label: t.getPaid.contributorManagement,
      description: language === "fr" ? "Demandes et gains des contributeurs." : "Contributor requests and earnings.",
      icon: "users",
    },
  ], [language, t.getPaid.configuration, t.getPaid.contributorManagement, t.getPaid.moderatorManagement]);

  const loadBudgetAndStats = async () => {
    if (!isAdminOrSuper(user?.role)) return;
    setLoadingBudget(true);
    try {
      const [budgetRes, statsRes] = await Promise.all([payoutsApi.getBudget(), payoutsApi.getPaymentStats()]);
      setBudgetInfo(budgetRes);
      setPaymentStats(statsRes);
    } catch {
      toast({ title: t.error, description: t.getPaid.budgetLoadError, variant: "destructive" });
    } finally {
      setLoadingBudget(false);
    }
  };

  // FeexPay confirms a deposit asynchronously: poll every 5 s for 2 minutes.
  const waitForBudgetConfirmation = async (previousBudget: number) => {
    for (let attempt = 0; attempt < 24; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 5000));
      try {
        const [budgetRes, statsRes] = await Promise.all([payoutsApi.getBudget(), payoutsApi.getPaymentStats()]);
        setBudgetInfo(budgetRes);
        setPaymentStats(statsRes);
        if (budgetRes.budget !== previousBudget) {
          toast({
            title: t.getPaid.budgetConfirmed,
            description: `Nouveau budget disponible: ${formatCurrency(budgetRes.budget, locale, currency)}`,
          });
          return;
        }
      } catch {
        return;
      }
    }
  };

  const handleDepositBudget = async () => {
    if (!isAdminOrSuper(user?.role)) return;
    setDepositForm(emptyDeposit());
    setDepositDialogOpen(true);
  };

  const handleSubmitDeposit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isAdminOrSuper(user?.role)) {
      toast({ title: t.getPaid.accessDenied, description: t.getPaid.depositAdminOnly, variant: "destructive" });
      return;
    }

    const amount = Math.round(Number(depositForm.amount));
    if (!Number.isFinite(amount) || amount <= 0) {
      toast({ title: t.amountInvalid, description: t.amountGreaterThanZero, variant: "destructive" });
      return;
    }

    const phone = depositForm.phone.trim();
    if (!phone) {
      toast({ title: t.getPaid.phoneRequiredTitle, description: t.getPaid.phoneRequiredDesc, variant: "destructive" });
      return;
    }

    setDepositLoading(true);
    try {
      const { firstName, lastName } = splitPayerName(user?.name);
      const res = await payoutsApi.depositAdmin({
        amount,
        phone,
        network: depositForm.network,
        email: (user as any)?.email || undefined,
        firstName,
        lastName,
        description: depositForm.description.trim() || undefined,
      });

      toast({
        title: t.getPaid.depositLaunched,
        description: `${formatCurrency(amount, locale, currency)} • Réf: ${res.txReference}. Confirmez la demande FeexPay envoyée au ${phone}, le budget sera mis à jour automatiquement.`,
      });

      setDepositDialogOpen(false);
      setDepositForm(emptyDeposit());
      loadBudgetAndStats();
      void waitForBudgetConfirmation(budgetInfo?.budget ?? 0);
    } catch (error: any) {
      toast({ title: t.error, description: apiErrorMessage(error, t.getPaid.depositFailed), variant: "destructive" });
    } finally {
      setDepositLoading(false);
    }
  };

  useEffect(() => {
    if (isAdminRole(user?.role)) {
      void payoutsApi
        .getPaymentSettings()
        .then((settings) => {
          setPayoutSettings((prev) => ({ ...prev, ...settings }));
        })
        .catch(() => {
          // The defaults stay on screen.
        });
    }
  }, [user?.role]);

  const handleSavePayoutSettings = async () => {
    try {
      const savedSettings = await payoutsApi.updatePaymentSettings(payoutSettings);
      setPayoutSettings((prev) => ({ ...prev, ...savedSettings }));
      toast({ title: t.getPaid.settingsSaved, description: t.getPaid.settingsSavedDesc });
    } catch (error: any) {
      toast({ title: t.error, description: apiErrorMessage(error, t.getPaid.saveSettingsFailed), variant: "destructive" });
    }
  };

  return {
    loadingBudget, budgetInfo, paymentStats, loadBudgetAndStats,
    depositDialogOpen, setDepositDialogOpen, depositForm, setDepositForm, depositLoading,
    handleDepositBudget, handleSubmitDeposit,
    payoutSettings, setPayoutSettings, handleSavePayoutSettings,
    adminTab, setAdminTab, adminNavigationItems,
  };
}
